import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { paymentMethodLabel } from '@/constants/service-payment-methods';
import { CHARGE_TIMELINE_STEPS } from '@/content/payments/charge';
import { Charge } from '@/types/charge';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format-date';

type StepState = 'done' | 'current' | 'pending';

interface TimelineStep {
  key: string;
  label: string;
  state: StepState;
  detail?: string | null;
}

/**
 * Enviada → Paga → Liberada → Na sua conta (plano, seção 6.6). Cada etapa
 * mostra a data que já existir; sem o dado (ex.: `availableOn`/`payoutId`
 * nulos até o repasse acontecer), a etapa fica pendente, nunca quebra.
 * `paidAt` só guarda dia (coluna `date` na API), por isso não tem horário
 * aqui como no exemplo do plano.
 */
export function ChargeTimeline({ charge }: { charge: Charge }) {
  const paidDetail = charge.paidAt
    ? [
        charge.paymentMethodUsed ? paymentMethodLabel(charge.paymentMethodUsed) : null,
        formatDate(charge.paidAt),
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  const availableOnDate = charge.availableOn ? new Date(charge.availableOn) : null;
  const isAvailable =
    !!availableOnDate &&
    !isNaN(availableOnDate.getTime()) &&
    availableOnDate.getTime() <= Date.now();

  const steps: TimelineStep[] = [
    {
      key: 'sent',
      label: CHARGE_TIMELINE_STEPS.sent,
      state: 'done',
      detail: formatDate(charge.createdAt),
    },
    {
      key: 'paid',
      label: CHARGE_TIMELINE_STEPS.paid,
      state: charge.paidAt ? 'done' : 'pending',
      detail: paidDetail,
    },
    {
      key: 'released',
      label: CHARGE_TIMELINE_STEPS.released,
      state: !charge.paidAt ? 'pending' : isAvailable ? 'done' : 'current',
      detail: availableOnDate ? formatDate(availableOnDate) : null,
    },
    {
      key: 'inAccount',
      label: CHARGE_TIMELINE_STEPS.inAccount,
      state: charge.payoutId ? 'done' : 'pending',
      detail: null,
    },
  ];

  return (
    <View>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const dotColor =
          step.state === 'done'
            ? 'bg-success-500'
            : step.state === 'current'
              ? 'bg-primary'
              : 'bg-neutral-300';
        const lineColor =
          step.state === 'pending' ? 'bg-neutral-200' : 'bg-success-300';
        const textColor =
          step.state === 'pending'
            ? 'text-muted-foreground'
            : 'text-foreground';

        return (
          <View key={step.key} className="flex-row gap-3">
            <View className="items-center" style={{ width: 12 }}>
              <View className={cn('h-3 w-3 rounded-full', dotColor)} />
              {!isLast ? <View className={cn('mt-0.5 w-[2px] flex-1', lineColor)} /> : null}
            </View>
            <View className={cn('flex-1', isLast ? 'pb-0' : 'pb-4')}>
              <ThemedText className={cn('font-semibold text-sm', textColor)}>
                {step.label}
              </ThemedText>
              {step.detail ? (
                <ThemedText className="text-muted-foreground text-xs">
                  {step.detail}
                </ThemedText>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}
